import { useState } from "react";
import { Link } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Badge, Button, Drawer, EmptyState } from "@/components/ui";
import { isUrgent, useStaffAlerts, type StaffAlert } from "./useStaffAlerts";

function ago(at: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const KIND_LABEL: Record<StaffAlert["kind"], string> = {
  ticket: "Support",
  reply: "Reply",
  application: "Application",
  report: "Report",
};

function badgeTone(alert: StaffAlert): "neutral" | "info" | "warning" | "danger" {
  if (alert.read) return "neutral";
  if (alert.kind === "report") return isUrgent(alert) ? "danger" : "warning";
  return "info";
}

/**
 * The top-bar Notifications button: a count of what is new, and the list behind
 * it — unread first, high-priority Trust & Safety reports at the top. Fed by
 * `useStaffAlerts`; see that file for what counts and why.
 */
export function NotificationsMenu() {
  const { alerts, unread, urgentUnread, markRead, markAllRead, clear } = useStaffAlerts();
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission,
  );

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread${urgentUnread ? `, ${urgentUnread} urgent` : ""}` : "Notifications"}
      >
        <Flex gap={8} align="center">
          <span>Notifications</span>
          {unread > 0 ? (
            <Badge tone={urgentUnread > 0 ? "danger" : "info"}>{unread > 99 ? "99+" : unread}</Badge>
          ) : null}
        </Flex>
      </Button>

      <Drawer open={open} onClose={() => setOpen(false)} title="Notifications">
        <Stack gap={12}>
          <Flex gap={8} wrap>
            {alerts.length > 0 ? (
              <>
                <Button variant="secondary" size="sm" onClick={markAllRead} disabled={unread === 0}>
                  Mark all read
                </Button>
                <Button variant="ghost" size="sm" onClick={clear}>
                  Clear
                </Button>
              </>
            ) : null}
            {permission === "default" ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void Notification.requestPermission().then(setPermission)}
              >
                Alert me when HQ is in the background
              </Button>
            ) : null}
          </Flex>

          {alerts.length === 0 ? (
            <EmptyState
              title="Nothing new"
              description="New reports, support tickets, customer replies and business applications appear here the moment they arrive, while HQ is open."
            />
          ) : (
            alerts.map((alert) => (
              <Link
                key={alert.key}
                to={alert.to}
                onClick={() => {
                  markRead(alert.key);
                  setOpen(false);
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Stack gap={4}>
                  <Flex gap={8} align="center">
                    <Badge tone={badgeTone(alert)}>{KIND_LABEL[alert.kind]}</Badge>
                    {alert.kind === "report" && alert.priority && isUrgent(alert) ? (
                      <Badge tone={alert.read ? "neutral" : "danger"}>{alert.priority}</Badge>
                    ) : null}
                    <Text role="small" color="secondary" as="span">
                      {ago(alert.at)}
                    </Text>
                  </Flex>
                  <Text role={alert.read ? "body" : "bodyStrong"} as="div">
                    {alert.title}
                  </Text>
                  <Text role="small" color="secondary" as="div">
                    {alert.detail}
                  </Text>
                </Stack>
              </Link>
            ))
          )}
        </Stack>
      </Drawer>
    </>
  );
}
